import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Match LVM.jsx ordering.
const LVM_DENOMS = [
  { key: "1", label: "$1", denom: 1 },
  { key: "5", label: "$5", denom: 5 },
  { key: "10", label: "$10", denom: 10 },
  { key: "20", label: "$20", denom: 20 },
  { key: "50", label: "$50", denom: 50 },
  { key: "100", label: "$100", denom: 100 },
  { key: "2", label: "$2", denom: 2 },
];

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Inline-styled section card to render a clean print look that does NOT depend
// on the app's runtime CSS variables (so prints look identical regardless of
// theme toggles, monitor, or browser zoom).
function PrintSection({ title, section, dateStr }) {
  const cashRows = LVM_DENOMS.map((d) => {
    const cnt = toNum(section.cash[d.key]);
    return { ...d, cnt, total: cnt * d.denom };
  });
  const totalCash = cashRows.reduce((s, r) => s + r.total, 0);
  const olTotal = toNum(section.reports.olTotal);
  const instTotal = toNum(section.reports.instTotal);
  const totalSLS = olTotal + instTotal;
  const online = toNum(section.reports.online);
  const instant = toNum(section.reports.instant);
  const credit = toNum(section.reports.creditCard);
  const coupon = toNum(section.reports.coupon);
  const cashInHand = totalSLS - (online + instant + credit + coupon);
  const mismatch = Math.round(totalCash) !== Math.round(cashInHand);

  // Color palette — chosen to match the user's screenshot (forest green, lime
  // green outer border, cream yellow cells, amber "Prizes Redeemed" banner).
  const colors = {
    outer: "#1f6b3d",
    bg: "#2c804c",
    headerBg: "#1f5e3a",
    headerText: "#ffffff",
    label: "#1f5e3a",
    labelText: "#1a4f30",
    cellBg: "#fff8d4",
    cellText: "#1a2b1d",
    grayBg: "#cfd1c8",
    grayText: "#4a4f4a",
    valueText: "#1a2b1d",
    prizeBg: "#f5a623",
    prizeText: "#2b1a00",
    border: "#0e3a22",
  };
  const cellStyle = {
    backgroundColor: colors.cellBg,
    color: colors.cellText,
    border: `1px solid ${colors.border}`,
    padding: "4px 6px",
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    fontSize: "12px",
  };
  const labelStyle = {
    backgroundColor: colors.label,
    color: colors.headerText,
    border: `1px solid ${colors.border}`,
    padding: "4px 6px",
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    fontSize: "12px",
    fontWeight: 700,
    textAlign: "center",
  };
  const denomLabelStyle = {
    ...labelStyle,
    color: "#3b54d6",
    backgroundColor: colors.grayBg,
    textAlign: "center",
  };
  const valueStyle = {
    ...cellStyle,
    backgroundColor: colors.grayBg,
    color: colors.valueText,
    textAlign: "right",
    fontWeight: 600,
  };
  const reportLabelStyle = {
    ...labelStyle,
    color: "#ffffff",
    backgroundColor: colors.label,
  };

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        border: `4px solid ${colors.outer}`,
        padding: "8px",
        flex: 1,
        minWidth: 0,
      }}
    >
      {/* Title bar */}
      <div
        style={{
          backgroundColor: "#3aa66b",
          color: "#1a2b1d",
          textAlign: "center",
          padding: "6px 0",
          fontFamily: "ui-monospace, Menlo, Consolas, monospace",
          fontWeight: 700,
          fontSize: "18px",
          letterSpacing: "0.1em",
          border: `2px solid ${colors.outer}`,
          marginBottom: "6px",
        }}
      >
        {title}
      </div>

      {/* Date */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "6px" }}>
        <tbody>
          <tr>
            <td style={{ ...labelStyle, textAlign: "right", width: "30%" }}>Date</td>
            <td style={{ ...cellStyle, textAlign: "center", fontWeight: 700 }}>
              {format(parseISO(dateStr), "M/d/yy")}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Two columns: cash count (left) + reports (right) */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {/* O/L Total row */}
          <tr>
            <td style={denomLabelStyle}>{cashRows[0].label}</td>
            <td style={cellStyle}>{cashRows[0].cnt || ""}</td>
            <td style={valueStyle}>{cashRows[0].total > 0 ? fmt(cashRows[0].total) : "$ -"}</td>
            <td style={reportLabelStyle}>O/L Total</td>
            <td style={valueStyle}>{fmt(olTotal)}</td>
          </tr>
          {/* INST Total row */}
          <tr>
            <td style={denomLabelStyle}>{cashRows[1].label}</td>
            <td style={cellStyle}>{cashRows[1].cnt || ""}</td>
            <td style={valueStyle}>{cashRows[1].total > 0 ? fmt(cashRows[1].total) : "$ -"}</td>
            <td style={reportLabelStyle}>INST Total</td>
            <td style={valueStyle}>{fmt(instTotal)}</td>
          </tr>
          {/* Total SLS row */}
          <tr>
            <td style={denomLabelStyle}>{cashRows[2].label}</td>
            <td style={cellStyle}>{cashRows[2].cnt || ""}</td>
            <td style={valueStyle}>{cashRows[2].total > 0 ? fmt(cashRows[2].total) : "$ -"}</td>
            <td style={reportLabelStyle}>Total SLS</td>
            <td style={{ ...valueStyle, fontWeight: 800 }}>{fmt(totalSLS)}</td>
          </tr>
          {/* Prizes Redeemed banner */}
          <tr>
            <td style={denomLabelStyle}>{cashRows[3].label}</td>
            <td style={cellStyle}>{cashRows[3].cnt || ""}</td>
            <td style={valueStyle}>{cashRows[3].total > 0 ? fmt(cashRows[3].total) : "$ -"}</td>
            <td
              colSpan={2}
              style={{
                backgroundColor: colors.prizeBg,
                color: colors.prizeText,
                fontWeight: 800,
                textAlign: "center",
                padding: "4px 6px",
                border: `1px solid ${colors.border}`,
                fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Prizes Redeemed
            </td>
          </tr>
          {/* Online prize */}
          <tr>
            <td style={denomLabelStyle}>{cashRows[4].label}</td>
            <td style={cellStyle}>{cashRows[4].cnt || ""}</td>
            <td style={valueStyle}>{cashRows[4].total > 0 ? fmt(cashRows[4].total) : "$ -"}</td>
            <td style={reportLabelStyle}>Online</td>
            <td style={valueStyle}>{fmt(online)}</td>
          </tr>
          {/* Instant prize */}
          <tr>
            <td style={denomLabelStyle}>{cashRows[5].label}</td>
            <td style={cellStyle}>{cashRows[5].cnt || ""}</td>
            <td style={valueStyle}>{cashRows[5].total > 0 ? fmt(cashRows[5].total) : "$ -"}</td>
            <td style={reportLabelStyle}>Instant</td>
            <td style={valueStyle}>{fmt(instant)}</td>
          </tr>
          {/* Credit Card */}
          <tr>
            <td style={denomLabelStyle}>{cashRows[6].label}</td>
            <td style={cellStyle}>{cashRows[6].cnt || ""}</td>
            <td style={valueStyle}>{cashRows[6].total > 0 ? fmt(cashRows[6].total) : "$ -"}</td>
            <td style={reportLabelStyle}>Credit Card</td>
            <td style={valueStyle}>{fmt(credit)}</td>
          </tr>
          {/* Coupon (no denom row on the left — keep alignment with a blank gray cell) */}
          <tr>
            <td style={{ ...denomLabelStyle, backgroundColor: colors.grayBg }}>&nbsp;</td>
            <td style={cellStyle}>&nbsp;</td>
            <td style={valueStyle}>&nbsp;</td>
            <td style={reportLabelStyle}>Coupons/Others</td>
            <td style={valueStyle}>{fmt(coupon)}</td>
          </tr>
          {/* Bottom totals */}
          <tr>
            <td colSpan={2} style={{ ...labelStyle, backgroundColor: colors.grayBg, color: colors.labelText, textAlign: "center" }}>
              Total Cash
            </td>
            <td style={{ ...valueStyle, fontWeight: 800 }}>{fmt(totalCash)}</td>
            <td style={{ ...labelStyle, backgroundColor: colors.grayBg, color: colors.labelText, textAlign: "center" }}>
              Cash In Hand
            </td>
            <td
              style={{
                ...valueStyle,
                fontWeight: 800,
                color: mismatch ? "#b00020" : colors.valueText,
                backgroundColor: mismatch ? "#ffd4d4" : colors.grayBg,
              }}
            >
              {fmt(cashInHand)}
            </td>
          </tr>
        </tbody>
      </table>

      {mismatch && (
        <div
          style={{
            marginTop: "6px",
            padding: "4px 6px",
            backgroundColor: "#ffd4d4",
            border: "1px solid #b00020",
            color: "#7a0014",
            fontFamily: "ui-monospace, Menlo, Consolas, monospace",
            fontSize: "11px",
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          MISMATCH — Total Cash ≠ Cash In Hand · Diff {fmt(Math.abs(totalCash - cashInHand))}
        </div>
      )}
    </div>
  );
}

export default function LVMPrint() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/lvm/${date}`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load LVM entry");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-fire the browser print dialog once content has rendered (gives the
  // user the standard "print preview" without an extra click). Skip on errors.
  useEffect(() => {
    if (!loading && data) {
      const t = setTimeout(() => window.print(), 350);
      return () => clearTimeout(t);
    }
  }, [loading, data]);

  if (loading) {
    return (
      <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>Loading LVM…</div>
    );
  }
  if (err) {
    return (
      <div style={{ padding: 24, fontFamily: "ui-monospace, monospace", color: "#b00020" }}>
        {err}
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#ffffff", padding: "16px" }}>
      {/* Screen-only toolbar */}
      <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="lvm-print-back">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="lvm-print-now">
          <Printer className="w-4 h-4 mr-1" />
          Print
        </Button>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h1
          style={{
            fontFamily: "ui-monospace, monospace",
            fontWeight: 700,
            fontSize: "20px",
            color: "#1a2b1d",
            marginBottom: "12px",
            textAlign: "center",
          }}
        >
          LVM Daily Report · {format(parseISO(date), "EEEE, MMM d, yyyy")}
        </h1>
        <div style={{ display: "flex", flexDirection: "row", gap: "12px" }}>
          <PrintSection title="LVM-1" section={data.lvm1} dateStr={date} />
          <PrintSection title="LVM-2" section={data.lvm2} dateStr={date} />
        </div>
      </div>

      {/* Print-only CSS: hide UI chrome, force colors */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #ffffff !important; }
          @page { size: letter landscape; margin: 0.4in; }
          /* Force background colors to print in Chrome/Edge */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}
